import * as Sentry from '@sentry/react';

import type { ActionContext } from 'js/lib/ActionContext';

import authoringStateActionsFactory from 'bundles/author-common/actions/AuthoringStateActions';
import { createWidgetSessionProvider } from 'bundles/author-common/utils/AuthoringWidgetSessionAPIUtils';
import RequestManager from 'bundles/author-common/utils/RequestManager';
import {
  getAuthoringWidgetItemDraft,
  updateAuthoringWidgetItemDraft,
} from 'bundles/author-widget/utils/AuthoringWidgetItemAPIUtils';
import { getResourceId } from 'bundles/author-widget/utils/WidgetCommonUtils';
import type { AuthoringWidgetSummariesV1 } from 'bundles/widget-admin/types/WidgetTypes';

import type { WidgetItemContent } from '../types/widgetItemContent';

const AuthoringStateActions = authoringStateActionsFactory('item');
const requestManager = new RequestManager();
const CONFLICT_ERROR_STATUS = 409;

export const update = (
  actionContext: ActionContext,
  branchId: string,
  itemId: string,
  atomId?: string,
  callback?: () => void
) => {
  return requestManager.debouncedRun(() => {
    const store = actionContext.getStore('WidgetStore');
    const widgetDraft: WidgetItemContent = store.getWidgetItemContent();
    const writeAccessToken = store.getWriteAccessToken();
    actionContext.executeAction(AuthoringStateActions.setInProgress);
    const authoringItemContentId = getResourceId(branchId, itemId, atomId);

    return updateAuthoringWidgetItemDraft(authoringItemContentId, widgetDraft, writeAccessToken)
      .then((response) => {
        const name = response.draft?.name;
        const providerId = response.draft?.content.providerId;
        const isGraded = response.draft?.content.isGraded;
        const newWriteAccessToken = response.writeAccessToken;
        const newWriteAccessState = response.writeAccessState;
        const timeCommitment = response.draft?.timeCommitment;

        actionContext.executeAction(AuthoringStateActions.setSuccess);
        actionContext.dispatch('WIDGET_DRAFT_UPDATED', {
          name,
          providerId,
          isGraded,
          timeCommitment,
        });
        actionContext.dispatch('WIDGET_WRITE_ACCESS_UPDATED', { newWriteAccessToken, newWriteAccessState });

        callback?.();
      })
      .catch((error) => {
        if (error.status === CONFLICT_ERROR_STATUS) {
          actionContext.executeAction(AuthoringStateActions.setConflict);
        } else {
          actionContext.executeAction(AuthoringStateActions.setError);
          actionContext.dispatch('WIDGET_AUTHORING_API_ERROR', error);
        }
      });
  });
};

/**
 * Sets up a widget item for a given course and branch.
 * This function retrieves the widget draft,
 * creates a session provider, and updates the widget draft.
 *
 * @param {Object} params - The parameters for setting up the widget
 * @param {string} params.branchId - The branch ID
 * @param {string} params.courseId - The course ID
 * @param {string} params.atomId - The atom ID
 * @param {string} params.itemId - The item ID
 * @param {Object} [params.widget] - The widget to set up
 * @returns {Promise<void>} A promise that resolves when the widget is set up
 */
export const setupWidgetItem = async ({
  branchId,
  courseId,
  atomId,
  itemId,
  widget,
}: {
  branchId: string;
  courseId: string;
  atomId: string;
  itemId: string;
  widget: AuthoringWidgetSummariesV1;
}) => {
  try {
    const authoringItemContentId = getResourceId(branchId, itemId, atomId);
    const authoringItemContent = await getAuthoringWidgetItemDraft(authoringItemContentId);
    const { draft, published, writeAccessToken } = authoringItemContent;
    const widgetItemContent = draft || published;

    const { id: widgetId, configurationExamples } = widget;
    const configuration = (configurationExamples[0] && configurationExamples[0].configuration) || {};

    const sessionProviderData = await createWidgetSessionProvider(widgetId, widget.name, courseId, configuration);
    const widgetSessionProviderDraft = sessionProviderData.elements[0];

    widgetItemContent.content.providerId = widgetSessionProviderDraft.id;
    widgetItemContent.content.isGraded = false;

    await updateAuthoringWidgetItemDraft(authoringItemContentId, widgetItemContent, writeAccessToken);

    return Promise.resolve();
  } catch (error) {
    Sentry.captureMessage(`Error setting up ${widget.name} widget`, { extra: { error } });
    throw error;
  }
};
